import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Card, CardMedia, Typography } from '@mui/material'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import { getImageUrlBySize } from 'src/Libs/shop/utils'
import { ShopImageThumnbailSizes } from 'src/Libs/shop/types'
import { Product } from 'src/Libs/shop/models/system-configuration-components.model'
import { useProductCardStyles } from '../../styles/product-card.styles'

interface QntmWarrantyProductCardProps {
  productData: Product
  isProductSelected: boolean
  onMoreInfo: (productId: string) => void
  onAddProductToCart: (productId: string) => void
  onRemoveProductFromCart: (productId: string) => void
}

/*
 * Displays an Warranty Product Card
 */
const QntmWarrantyProductCard: React.FC<QntmWarrantyProductCardProps> = ({
  productData,
  onMoreInfo,
  isProductSelected,
  onAddProductToCart,
  onRemoveProductFromCart,
}) => {
  const { t } = useCustomTranslation()
  const { classes, cx } = useProductCardStyles()
  const unitPrice = currencyFormatter(productData.calculatedPrice.unitPrice)
  const price = currencyFormatter(productData?.calculatedPrice?.listPrice?.price) ?? currencyFormatter(productData?.calculatedPrice.unitPrice)
  const productImageUrl = getImageUrlBySize(productData?.cover?.media?.thumbnails, ShopImageThumnbailSizes.Medium)
  const isPriceApplied = Boolean(productData.extensions?.e3dcPriceApplied?.state)

  const handleAddProductToCart = (): void => {
    onAddProductToCart(productData.id)
  }

  const handleRemoveProductFromCart = (): void => {
    onRemoveProductFromCart(productData.id)
  }

  return (
    <Card className={cx(classes.product, isProductSelected && classes.productActive)} key={productData.id}>
      {isProductSelected && <Typography className={classes.activeLabel}>{t('shop.common.selected')}</Typography>}
      <CardMedia
        className={classes.productImage}
        component="img"
        image={productImageUrl}
        alt="Product"
        onClick={() => {
          onMoreInfo(productData.id)
        }}
      />
      <Box className={classes.containerTextWrapper}>
        <Typography align="left" variant="h3">
          {productData.translated.name}
        </Typography>
      </Box>
      <Box>
        <Box className={classes.priceContainer}>
          <Box className={classes.flexPrice}>
            <Typography color={unitPrice < price ? 'primary' : 'inherit'} variant="h5">
              {unitPrice || price}
            </Typography>
            {unitPrice < price && (
              <Typography ml={4} sx={{ textDecorationLine: 'line-through' }} variant="h5">
                {price}
              </Typography>
            )}
          </Box>
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            className={classes.button}
            size="large"
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={() => {
              onMoreInfo(productData.id)
            }}
          >
            {t('shop.common.more-info')}
          </Button>
          {isProductSelected ? (
            <Button
              className={classes.button}
              disabled={!isPriceApplied}
              color="inherit"
              variant="contained"
              fullWidth
              onClick={handleRemoveProductFromCart}
            >
              {t('shop.common.remove')}
            </Button>
          ) : (
            <Button
              className={classes.button}
              disabled={!isPriceApplied}
              color="primary"
              variant="contained"
              fullWidth
              onClick={handleAddProductToCart}
            >
              {t('shop.common.select')}
            </Button>
          )}
        </Box>
      </Box>
    </Card>
  )
}

export default QntmWarrantyProductCard
